import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NotifierService } from 'angular-notifier';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AnyIntegration, IIntegrationBostonPizzaUI } from '../../../../interfaces/restaurant';

const urlRegex = new RegExp(
  '^(https?:\\/\\/)?' +
  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
  '((\\d{1,3}\\.){3}\\d{1,3}))' +
  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
  '(\\?[;&a-z\\d%_.~[\\]+=\\-/,]*)?' +
  '(\\#[-a-z\\d_]*)?$', 'i'
);

type EditIntegrationBpUiForm = {
  proxy: FormControl<boolean>;
  proxyUnifiedMode: FormControl<boolean>;
  controlMusicUrl: FormControl<string>;
  googleReviewsUrl: FormControl<string>;
  guestSurveyUrl: FormControl<string>;
  notAcceptingRewardsCardsDisclaimer: FormControl<boolean>;
  happyHourPilot: FormControl<boolean>;
  interacBanner: FormControl<boolean>;
};

@UntilDestroy()
@Component({
  selector: 'app-edit-integration-bp-ui',
  templateUrl: './edit-integration-bp-ui.component.html',
  styleUrls: ['./edit-integration-bp-ui.component.scss']
})
export class EditIntegrationBpUiComponent implements OnInit {

  @Input() set data(value: AnyIntegration) {

    if (value && value.provider === 'bostonPizzaUI') {
      const bpValue = value as IIntegrationBostonPizzaUI;

      this.editIntegrationForm.get('proxy')?.setValue(bpValue.proxy ?? false, { emitEvent: false });
      const proxyUnifiedModeControl = this.editIntegrationForm.get('proxyUnifiedMode');

      if (bpValue.proxy) {
        proxyUnifiedModeControl?.enable({ emitEvent: false });
        proxyUnifiedModeControl?.setValue(bpValue.proxyUnifiedMode ?? false, { emitEvent: false });
      } else {
        proxyUnifiedModeControl?.disable({ emitEvent: false });
        proxyUnifiedModeControl?.setValue(false, { emitEvent: false });
      }

      this.editIntegrationForm.patchValue({
        controlMusicUrl: bpValue.credentials.controlMusicUrl ?? '',
        googleReviewsUrl: bpValue.credentials.googleReviewsUrl ?? '',
        guestSurveyUrl: bpValue.credentials.guestSurveyUrl ?? '',
        notAcceptingRewardsCardsDisclaimer: bpValue.credentials.notAcceptingRewardsCardsDisclaimer ?? false,
        happyHourPilot: bpValue.credentials.happyHourPilot ?? false,
        interacBanner: bpValue.credentials.interacBanner ?? false,
      });
    }
  }

  public editIntegrationForm: FormGroup<EditIntegrationBpUiForm> = new FormGroup({
      proxy: new FormControl<boolean>(false, { nonNullable: true, validators: [Validators.required] }),
      proxyUnifiedMode: new FormControl(
        {value: false, disabled: true},
        { nonNullable: true, validators: [Validators.required] }
      ),
      controlMusicUrl: new FormControl('', {
        nonNullable: true,
        validators: [Validators.pattern(urlRegex), Validators.maxLength(2048)]
      }),
      googleReviewsUrl: new FormControl('', {
        nonNullable: true,
        validators: [Validators.pattern(urlRegex), Validators.maxLength(2048)]
      }),
      guestSurveyUrl: new FormControl('', {
        nonNullable: true,
        validators: [Validators.pattern(urlRegex), Validators.maxLength(2048)]
      }),
      notAcceptingRewardsCardsDisclaimer: new FormControl<boolean>(false, { nonNullable: true }),
      happyHourPilot: new FormControl<boolean>(false, { nonNullable: true }),
      interacBanner: new FormControl<boolean>(false, { nonNullable: true })
    }
  );

  @Output() edited = new EventEmitter<Partial<AnyIntegration>>();
  @Output() remove = new EventEmitter<void>();

  constructor(
    public dialogRef: MatDialogRef<any>,
    public notifier: NotifierService,
  ) {}

  ngOnInit(): void {
    this.addListeners();
  }

  private addListeners(): void {
    this.editIntegrationForm.get('proxy')?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe((proxyValue: boolean) => {
      const proxyUnifiedModeControl = this.editIntegrationForm.get('proxyUnifiedMode');

      if (!proxyValue) {
        proxyUnifiedModeControl?.disable({emitEvent: false});
        proxyUnifiedModeControl?.setValue(false, { emitEvent: false });
      } else {
        proxyUnifiedModeControl?.enable({emitEvent: false});
      }
    });
  }

  public edit(): void {
    const formValue = this.editIntegrationForm.getRawValue();

    this.edited.emit({
      proxy: formValue.proxy,
      proxyUnifiedMode: formValue.proxyUnifiedMode,
      credentials: {
        controlMusicUrl: formValue.controlMusicUrl,
        googleReviewsUrl: formValue.googleReviewsUrl,
        guestSurveyUrl: formValue.guestSurveyUrl,
        notAcceptingRewardsCardsDisclaimer: formValue.notAcceptingRewardsCardsDisclaimer,
        happyHourPilot: formValue.happyHourPilot,
        interacBanner: formValue.interacBanner
      }
    });
  }

  public removeIntegration(): void {
    this.remove.emit();
  }
}
